import { jsPDF } from 'jspdf'
import html2canvas from 'html2canvas'
import { fetchFeeReceiptTemplate, defaultFeeReceiptTemplate } from '../../../settings/fee-receipt/api/feeReceiptTemplateApi'
import { numberToWords, formatIndianCurrency } from '../../../settings/fee-receipt/utils/numberToWords'
import { readSchoolLogo } from '../../../../shared/utils/schoolLogoStorage'
import { showToastMessage } from '../../../../components/Toast'

export interface ReceiptTransactionInput {
  id?: string
  receiptNo?: string
  studentName?: string
  studentId?: string
  className?: string
  amount?: number
  date?: string
  mode?: string
  status?: string
  referenceNo?: string
  feeType?: string
  parentName?: string
  parentPhone?: string
  parentRelationship?: string
}

export interface StudentFeeReportInput {
  studentName: string
  studentId: string
  className: string
  academicYear?: string
  feeRows: Array<{
    id?: string
    feeHead: string
    installment: string
    dueAmount: number
    paid: number
    outstanding: number
    status: 'Paid' | 'Partial' | 'Unpaid'
  }>
}

function cleanStr(val: unknown, fallback = '-'): string {
  if (val === undefined || val === null || String(val).trim() === '') return fallback
  return String(val).trim()
}

/** Global Idempotency Lock for PDF Generation */
let isPdfGenerating = false

/**
 * Common PDF generation engine using the EXACT Settings Fee Receipt Template configuration.
 * Enforces strict idempotency to prevent duplicate downloads on rapid double clicks.
 */
async function renderSettingsTemplatePDF(
  options: {
    docTitle: string
    docNo: string
    docDate: string
    sessionStr: string
    studentName: string
    studentId: string
    className: string
    parentName?: string
    parentPhone?: string
    parentRelationship?: string
    feeItems: Array<{
      sl: string
      desc: string
      installment?: string
      due: number
      paid: number
      outstanding?: number
      status?: string
    }>
    showOutstandingCol?: boolean
    paymentModeStr?: string
    bankAuthCode?: string
    fileName: string
    action?: 'download' | 'print' | 'view'
  }
): Promise<void> {
  if (isPdfGenerating) {
    showToastMessage('PDF generation in progress. Please wait...', 'info')
    return
  }

  isPdfGenerating = true
  let container: HTMLDivElement | null = null

  try {
    // 1. Fetch active configured template settings from Settings / localStorage / API
    let config = defaultFeeReceiptTemplate
    try {
      config = await fetchFeeReceiptTemplate()
    } catch (err) {
      console.warn('Failed to fetch active template config, using default:', err)
      config = defaultFeeReceiptTemplate
    }

    const {
      institution,
      receiptFormat,
      receiptLayout = 'minimal',
      includedFields,
      typography,
      footer,
    } = config || {}

    const uploadedLogo = readSchoolLogo()
    const activeLogoUrl = uploadedLogo || institution?.logoUrl || null

    const schoolName = cleanStr(institution?.schoolName) !== '-' ? cleanStr(institution?.schoolName) : "ST. XAVIER'S GLOBAL SCHOOL"
    const schoolAddress = cleanStr(institution?.address) !== '-' ? cleanStr(institution?.address) : '12th Avenue, Knowledge Park III, Greater Noida, UP - 201310'
    const schoolPhone = cleanStr(institution?.contactPhone) !== '-' ? cleanStr(institution?.contactPhone) : '-'
    const schoolEmail = cleanStr(institution?.email) !== '-' ? cleanStr(institution?.email) : '-'

    const fontFam =
      typography?.fontFamily === 'Roboto'
        ? 'Roboto, sans-serif'
        : typography?.fontFamily === 'Outfit'
        ? 'Outfit, sans-serif'
        : typography?.fontFamily === 'Georgia'
        ? 'Georgia, serif'
        : 'Inter, sans-serif'

    const isCentered = typography?.alignment === 'Center'
    const showLogo = institution?.showLogo !== false

    const totalPaidAmount = options.feeItems.reduce((sum, item) => sum + (Number(item.paid) || 0), 0)
    const totalDueAmount = options.feeItems.reduce((sum, item) => sum + (Number(item.due) || 0), 0)
    const totalOutstandingAmount = options.feeItems.reduce((sum, item) => sum + (Number(item.outstanding) || 0), 0)

    let totalWords = 'Zero Rupees Only'
    try {
      totalWords = numberToWords(totalPaidAmount || totalDueAmount)
    } catch {
      totalWords = `${totalPaidAmount || totalDueAmount} Rupees Only`
    }

    const isStudentDetailsVisible = includedFields?.studentDetails !== false && includedFields?.studentName !== false
    const isParentDetailsVisible = includedFields?.parentDetails !== false && includedFields?.parentName !== false

    // Build Header HTML according to selected layout settings (Classic / Minimal / Modern)
    let headerHtml = ''
    if (receiptLayout === 'classic') {
      headerHtml = `
        <div style="text-align: ${isCentered ? 'center' : 'left'}; margin-bottom: 12px; padding-bottom: 4px;">
          ${showLogo ? `
            <div style="width: 56px; height: 56px; border-radius: 50%; border: 2px solid #334155; background: #f8fafc; display: flex; align-items: center; justify-content: center; margin: ${isCentered ? '0 auto 8px auto' : '0 0 8px 0'}; overflow: hidden;">
              ${activeLogoUrl ? `<img src="${activeLogoUrl}" style="width: 100%; height: 100%; object-fit: contain; padding: 2px;" alt="School Logo" />` : `<span style="font-weight: bold; font-size: 24px; color: #334155;">🏫</span>`}
            </div>
          ` : ''}
          <div>
            <h1 style="margin: 0; font-size: 17px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: #0f172a; font-family: Georgia, serif;">${schoolName}</h1>
            <p style="margin: 3px 0 0 0; font-size: 10px; color: #475569; font-weight: 500;">${schoolAddress}</p>
            <p style="margin: 2px 0 0 0; font-size: 9.5px; color: #64748b;">${schoolPhone !== '-' ? `Ph: ${schoolPhone}` : ''}${schoolEmail !== '-' ? ` | Email: ${schoolEmail}` : ''}</p>
          </div>

          <div style="margin-top: 10px; border-top: 2px solid #1e293b; border-bottom: 2px solid #1e293b; padding: 5px 8px; display: flex; justify-content: space-between; align-items: center; font-size: 10px; font-weight: 700; color: #0f172a; text-transform: uppercase;">
            <span>${options.docTitle}</span>
            ${includedFields?.receiptNumber !== false ? `<span>NO: ${options.docNo}</span>` : ''}
            ${includedFields?.paymentDate !== false ? `<span>DATE: ${options.docDate}</span>` : ''}
            <span>SESSION: ${options.sessionStr}</span>
          </div>
        </div>
      `
    } else if (receiptLayout === 'minimal') {
      headerHtml = `
        <div style="display: flex; justify-content: space-between; align-items: flex-start; padding-bottom: 10px; border-bottom: 1px solid #e2e8f0; margin-bottom: 12px;">
          <div style="display: flex; align-items: center; gap: 12px;">
            ${showLogo ? `
              <div style="width: 40px; height: 40px; border-radius: 6px; background: #f1f5f9; display: flex; align-items: center; justify-content: center; overflow: hidden;">
                ${activeLogoUrl ? `<img src="${activeLogoUrl}" style="width: 100%; height: 100%; object-fit: contain; padding: 2px;" alt="School Logo" />` : `<span style="font-size: 20px;">🏫</span>`}
              </div>
            ` : ''}
            <div>
              <h1 style="margin: 0; font-size: 15px; font-weight: 700; text-transform: uppercase; color: #0f172a;">${schoolName}</h1>
              <p style="margin: 2px 0 0 0; font-size: 9.5px; color: #64748b;">${schoolAddress}</p>
              <p style="margin: 1px 0 0 0; font-size: 9px; color: #94a3b8;">${schoolPhone !== '-' ? `Ph: ${schoolPhone}` : ''}${schoolEmail !== '-' ? ` | Email: ${schoolEmail}` : ''}</p>
            </div>
          </div>

          <div style="text-align: right; font-size: 9.5px; color: #475569;">
            <span style="background: #f1f5f9; color: #0f172a; font-weight: 700; padding: 2px 8px; border-radius: 4px; display: inline-block; text-transform: uppercase; font-size: 10px; margin-bottom: 4px;">${options.docTitle}</span>
            ${includedFields?.receiptNumber !== false ? `<div>No #${options.docNo}</div>` : ''}
            ${includedFields?.paymentDate !== false ? `<div>Date: ${options.docDate}</div>` : ''}
            <div>Session: ${options.sessionStr}</div>
          </div>
        </div>
      `
    } else {
      /* MODERN TEAL GRADIENT LAYOUT (DEFAULT) */
      headerHtml = `
        <div style="background: linear-gradient(to right, #006B73, #0B858E); color: #ffffff; padding: 16px; border-radius: 12px; display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 14px; box-shadow: 0 1px 3px rgba(0,0,0,0.05);">
          <div style="display: flex; align-items: center; gap: 12px;">
            ${showLogo ? `
              <div style="width: 48px; height: 48px; border-radius: 8px; border: 1px solid rgba(255,255,255,0.2); background: rgba(255,255,255,0.1); display: flex; align-items: center; justify-content: center; overflow: hidden; shrink: 0;">
                ${activeLogoUrl ? `<img src="${activeLogoUrl}" style="width: 100%; height: 100%; object-fit: contain; padding: 2px;" alt="School Logo" />` : `<span style="font-size: 22px; color: #ffffff;">🏫</span>`}
              </div>
            ` : ''}
            <div>
              <h1 style="margin: 0; font-size: 17px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.5px; color: #ffffff; line-height: 1.2;">${schoolName}</h1>
              <p style="margin: 3px 0 0 0; font-size: 10px; color: #ccfbf1; font-weight: 500;">${schoolAddress}</p>
              <p style="margin: 2px 0 0 0; font-size: 9.5px; color: #99f6e4;">${schoolPhone !== '-' ? `Ph: ${schoolPhone}` : ''}${schoolEmail !== '-' ? ` | Email: ${schoolEmail}` : ''}</p>
            </div>
          </div>

          <div style="text-align: right;">
            <h2 style="margin: 0; font-size: 18px; font-weight: 900; text-transform: uppercase; letter-spacing: 1px; color: #ffffff; line-height: 1;">${options.docTitle}</h2>
            ${includedFields?.receiptNumber !== false ? `<div style="font-size: 10px; color: #ccfbf1; margin-top: 4px;">No: <span style="font-weight: 700; color: #ffffff;">${options.docNo}</span></div>` : ''}
            ${includedFields?.paymentDate !== false ? `<div style="font-size: 10px; color: #ccfbf1; margin-top: 2px;">Date: <span style="font-weight: 600; color: #ffffff;">${options.docDate}</span></div>` : ''}
            <div style="font-size: 10px; color: #ccfbf1; margin-top: 2px;">Session: <span style="font-weight: 600; color: #ffffff;">${options.sessionStr}</span></div>
          </div>
        </div>
      `
    }

    container = document.createElement('div')
    container.style.position = 'absolute'
    container.style.left = '-9999px'
    container.style.top = '-9999px'
    container.style.width = '794px'
    container.style.backgroundColor = '#ffffff'
    container.style.fontFamily = fontFam
    container.style.color = '#263238'
    container.style.boxSizing = 'border-box'

    container.innerHTML = `
      <div style="padding: 18px; box-sizing: border-box; background: #ffffff; border: 1px solid #006B73; width: 794px; page-break-inside: avoid; break-inside: avoid;">
        ${headerHtml}

        ${receiptLayout === 'modern' ? `<div style="border-bottom: 1px solid #D9E1E5; width: 100%; margin-bottom: 14px;"></div>` : ''}

        <!-- STUDENT & PARENT DETAILS SECTION -->
        ${(isStudentDetailsVisible || isParentDetailsVisible || includedFields?.classSection !== false) ? `
          <div style="display: grid; grid-template-columns: ${isParentDetailsVisible && options.parentName !== '-' ? '1fr 1fr' : '1fr'}; gap: 16px; margin-bottom: 14px; font-size: 10px;">
            ${isStudentDetailsVisible ? `
              <div style="space-y: 4px;">
                <div style="display: flex; align-items: center; gap: 6px; color: #006B73; font-weight: 700; font-size: 9.5px; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 1px solid rgba(0,107,115,0.2); padding-bottom: 4px;">
                  <span>STUDENT DETAILS</span>
                </div>
                <div style="padding-top: 2px;">
                  <div style="display: flex; justify-content: space-between; align-items: center; padding: 3px 0; border-bottom: 1px solid #f1f5f9;">
                    <span style="color: #64748B; font-size: 9.5px;">Name</span>
                    <span style="color: #263238; font-weight: 600; font-size: 10px;">${options.studentName}</span>
                  </div>
                  <div style="display: flex; justify-content: space-between; align-items: center; padding: 3px 0; border-bottom: 1px solid #f1f5f9;">
                    <span style="color: #64748B; font-size: 9.5px;">Adm No</span>
                    <span style="color: #263238; font-weight: 600; font-size: 10px;">${options.studentId}</span>
                  </div>
                  ${includedFields?.classSection !== false ? `
                    <div style="display: flex; justify-content: space-between; align-items: center; padding: 3px 0; border-bottom: 1px solid #f1f5f9;">
                      <span style="color: #64748B; font-size: 9.5px;">Class & Section</span>
                      <span style="color: #263238; font-weight: 600; font-size: 10px;">${options.className}</span>
                    </div>
                  ` : ''}
                </div>
              </div>
            ` : ''}

            ${isParentDetailsVisible && options.parentName !== '-' ? `
              <div style="space-y: 4px;">
                <div style="display: flex; align-items: center; gap: 6px; color: #006B73; font-weight: 700; font-size: 9.5px; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 1px solid rgba(0,107,115,0.2); padding-bottom: 4px;">
                  <span>PARENT DETAILS</span>
                </div>
                <div style="padding-top: 2px;">
                  <div style="display: flex; justify-content: space-between; align-items: center; padding: 3px 0; border-bottom: 1px solid #f1f5f9;">
                    <span style="color: #64748B; font-size: 9.5px;">Name</span>
                    <span style="color: #263238; font-weight: 600; font-size: 10px;">${options.parentName}</span>
                  </div>
                  ${options.parentPhone !== '-' ? `
                    <div style="display: flex; justify-content: space-between; align-items: center; padding: 3px 0; border-bottom: 1px solid #f1f5f9;">
                      <span style="color: #64748B; font-size: 9.5px;">Contact Number</span>
                      <span style="color: #263238; font-weight: 600; font-size: 10px;">${options.parentPhone}</span>
                    </div>
                  ` : ''}
                  ${options.parentRelationship !== '-' ? `
                    <div style="display: flex; justify-content: space-between; align-items: center; padding: 3px 0;">
                      <span style="color: #64748B; font-size: 9.5px;">Relationship</span>
                      <span style="color: #263238; font-weight: 600; font-size: 10px;">${options.parentRelationship}</span>
                    </div>
                  ` : ''}
                </div>
              </div>
            ` : ''}
          </div>
        ` : ''}

        <!-- FEE BREAKDOWN TABLE -->
        ${includedFields?.feeBreakdownTable !== false ? `
          <div style="margin-bottom: 14px;">
            <div style="border: 1px solid #D9E1E5; border-radius: 4px; overflow: hidden;">
              <table style="width: 100%; border-collapse: collapse; text-align: left; font-size: 10px;">
                <thead>
                  <tr style="font-size: 9px; font-weight: 700; text-transform: uppercase; background: ${receiptLayout === 'classic' ? '#1e293b' : receiptLayout === 'minimal' ? '#f1f5f9' : '#006B73'}; color: ${receiptLayout === 'minimal' ? '#1e293b' : '#ffffff'};">
                    <th style="padding: 6px 8px; width: 32px; text-align: center; border-right: 1px solid rgba(255,255,255,0.2);">#</th>
                    <th style="padding: 6px 8px; border-right: 1px solid rgba(255,255,255,0.2);">FEE DESCRIPTION</th>
                    <th style="padding: 6px 8px; text-align: right; width: 110px; border-right: 1px solid rgba(255,255,255,0.2);">DUE AMT (₹)</th>
                    <th style="padding: 6px 8px; text-align: right; width: 110px; ${options.showOutstandingCol ? 'border-right: 1px solid rgba(255,255,255,0.2);' : ''}">PAID AMT (₹)</th>
                    ${options.showOutstandingCol ? `<th style="padding: 6px 8px; text-align: right; width: 110px;">OUTSTANDING (₹)</th>` : ''}
                  </tr>
                </thead>
                <tbody>
                  ${options.feeItems.map((item, idx) => `
                    <tr style="background: ${idx % 2 === 1 ? '#F8FAFC' : '#ffffff'}; border-bottom: 1px solid #D9E1E5;">
                      <td style="padding: 5px 8px; text-align: center; color: #64748B; font-weight: 500; font-size: 9.5px; border-right: 1px solid #D9E1E5;">${item.sl}</td>
                      <td style="padding: 5px 8px; font-weight: 600; color: #263238; border-right: 1px solid #D9E1E5;">${cleanStr(item.desc)}</td>
                      <td style="padding: 5px 8px; text-align: right; font-weight: 600; color: #263238; border-right: 1px solid #D9E1E5;">${formatIndianCurrency(item.due)}</td>
                      <td style="padding: 5px 8px; text-align: right; font-weight: 600; color: #15803d; ${options.showOutstandingCol ? 'border-right: 1px solid #D9E1E5;' : ''}">${formatIndianCurrency(item.paid)}</td>
                      ${options.showOutstandingCol ? `<td style="padding: 5px 8px; text-align: right; font-weight: 700; color: ${(item.outstanding || 0) > 0 ? '#b91c1c' : '#475569'};">${formatIndianCurrency(item.outstanding || 0)}</td>` : ''}
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            </div>

            <div style="display: flex; justify-content: space-between; align-items: center; padding: 8px 12px; background: #F5F7F8; border-left: 1px solid #D9E1E5; border-right: 1px solid #D9E1E5; border-bottom: 1px solid #D9E1E5; border-radius: 0 0 4px 4px;">
              <div>
                <span style="font-size: 8.5px; text-transform: uppercase; font-weight: 700; color: #64748B; display: block;">TOTAL IN WORDS</span>
                <span style="font-size: 10px; font-weight: 700; color: #263238; text-transform: capitalize;">${totalWords}</span>
              </div>
              <div style="text-align: right; font-size: 12px; font-weight: 800; color: #006B73;">
                ${includedFields?.totalAmountPaid !== false ? `Total Paid: <span style="font-weight: 800;">₹${formatIndianCurrency(totalPaidAmount)}</span>` : ''}
                ${options.showOutstandingCol && totalOutstandingAmount > 0 ? `<div style="font-size: 10.5px; color: #b91c1c; margin-top: 2px;">Net Outstanding: <span>₹${formatIndianCurrency(totalOutstandingAmount)}</span></div>` : ''}
              </div>
            </div>
          </div>
        ` : ''}

        <!-- PAY MODE INFORMATION -->
        ${options.paymentModeStr && options.paymentModeStr !== '-' ? `
          <div style="margin-bottom: 14px;">
            <div style="color: #006B73; font-weight: 700; font-size: 9.5px; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px;">
              PAY MODE INFORMATION
            </div>
            <div style="border-radius: 4px; border: 1px solid #D9E1E5; background: #F5F7F8; padding: 10px; display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 8px; font-size: 10px;">
              ${includedFields?.paymentMode !== false ? `
                <div>
                  <span style="font-size: 8.5px; color: #64748B; text-transform: uppercase; font-weight: 500; display: block;">PAY MODE</span>
                  <span style="color: #263238; font-weight: 700; display: block;">${options.paymentModeStr}</span>
                </div>
              ` : ''}
              <div>
                <span style="font-size: 8.5px; color: #64748B; text-transform: uppercase; font-weight: 500; display: block;">DATE</span>
                <span style="color: #263238; font-weight: 700; display: block;">${options.docDate}</span>
              </div>
              <div>
                <span style="font-size: 8.5px; color: #64748B; text-transform: uppercase; font-weight: 500; display: block;">BANK AUTH CODE</span>
                <span style="color: #263238; font-weight: 700; display: block; font-family: monospace;">${options.bankAuthCode || options.docNo}</span>
              </div>
            </div>
          </div>
        ` : ''}

        <!-- TERMS & CONDITIONS / NOTES SECTION -->
        ${footer?.termsAndConditions ? `
          <div style="padding-top: 6px; border-top: 1px solid #D9E1E5; margin-bottom: 14px;">
            <div style="color: #006B73; font-weight: 700; font-size: 9.5px; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 2px;">
              TERMS & CONDITIONS
            </div>
            <p style="margin: 0; font-size: 8.5px; color: #64748B; line-height: 1.4; white-space: pre-line;">${cleanStr(footer.termsAndConditions)}</p>
          </div>
        ` : ''}

        <!-- CLEAN FOOTER SECTION -->
        <div style="padding-top: 12px; border-top: 1px solid #D9E1E5; text-align: center; font-size: 9px;">
          <p style="margin: 0; font-size: 11px; font-weight: 700; color: #006B73;">Thank you for your payment!</p>
          <p style="margin: 2px 0 0 0; font-size: 8.5px; color: #64748B;">© 2026 ${schoolName} ERP. System Version: 4.2.1</p>
        </div>

      </div>
    `

    document.body.appendChild(container)

    const canvas = await html2canvas(container, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#ffffff',
      logging: false,
    })

    const imgData = canvas.toDataURL('image/png')

    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    })

    const pdfWidth = pdf.internal.pageSize.getWidth()
    const pdfPageHeight = pdf.internal.pageSize.getHeight()
    let imgWidth = pdfWidth
    let imgHeight = (canvas.height * pdfWidth) / canvas.width

    // Guarantee receipt fits strictly on 1 single page without spilling onto page 2
    if (imgHeight > pdfPageHeight) {
      const scaleRatio = pdfPageHeight / imgHeight
      imgHeight = pdfPageHeight
      imgWidth = pdfWidth * scaleRatio
    }
    const xPos = (pdfWidth - imgWidth) / 2

    pdf.addImage(imgData, 'PNG', xPos, 0, imgWidth, imgHeight)

    if (options.action === 'print') {
      pdf.autoPrint()
      const blobUrl = String(pdf.output('bloburl'))
      const printWindow = window.open(blobUrl, '_blank')
      if (printWindow) {
        printWindow.focus()
      } else {
        const iframe = document.createElement('iframe')
        iframe.style.position = 'fixed'
        iframe.style.right = '0'
        iframe.style.bottom = '0'
        iframe.style.width = '0'
        iframe.style.height = '0'
        iframe.style.border = '0'
        iframe.src = blobUrl
        document.body.appendChild(iframe)
        setTimeout(() => {
          iframe.contentWindow?.focus()
          iframe.contentWindow?.print()
        }, 500)
      }
    } else if (options.action === 'view') {
      const blobUrl = String(pdf.output('bloburl'))
      const viewWin = window.open(blobUrl, '_blank')
      if (!viewWin) {
        pdf.save(`${options.fileName}.pdf`)
      }
    } else {
      pdf.save(`${options.fileName}.pdf`)
    }
  } catch (error) {
    console.error('Error generating PDF:', error)
    throw error
  } finally {
    isPdfGenerating = false
    if (container && document.body.contains(container)) {
      document.body.removeChild(container)
    }
  }
}

/**
 * Convert HTML string content into a binary A4 PDF Blob.
 */
export async function convertHtmlToPdfBlob(htmlContent: string): Promise<Blob> {
  const container = document.createElement('div')
  container.style.position = 'fixed'
  container.style.left = '-9999px'
  container.style.top = '-9999px'
  container.style.width = '800px'
  container.style.background = '#ffffff'
  container.style.padding = '16px'
  container.style.boxSizing = 'border-box'
  container.innerHTML = htmlContent
  document.body.appendChild(container)

  try {
    let canvas: HTMLCanvasElement
    try {
      canvas = await html2canvas(container, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
      })
    } catch {
      canvas = document.createElement('canvas')
      canvas.width = 800
      canvas.height = 1000
      const ctx = canvas.getContext('2d')
      if (ctx) {
        ctx.fillStyle = '#ffffff'
        ctx.fillRect(0, 0, 800, 1000)
      }
    }

    let imgData = ''
    try {
      imgData = canvas.toDataURL('image/png')
    } catch {
      imgData = ''
    }

    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    })

    if (imgData && imgData.startsWith('data:image')) {
      const pdfWidth = pdf.internal.pageSize.getWidth()
      const pdfPageHeight = pdf.internal.pageSize.getHeight()
      let imgWidth = pdfWidth
      let imgHeight = (canvas.height * pdfWidth) / canvas.width

      if (imgHeight > pdfPageHeight) {
        const scaleRatio = pdfPageHeight / imgHeight
        imgHeight = pdfPageHeight
        imgWidth = pdfWidth * scaleRatio
      }
      const xPos = (pdfWidth - imgWidth) / 2

      pdf.addImage(imgData, 'PNG', xPos, 0, imgWidth, imgHeight)
    } else {
      pdf.setFontSize(10)
      pdf.text(htmlContent.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 500), 10, 10)
    }

    return pdf.output('blob')
  } finally {
    if (container && document.body.contains(container)) {
      document.body.removeChild(container)
    }
  }
}

/**
 * Parses response from server (which could be binary PDF, raw HTML, or Base64 encoded HTML string)
 * and returns a guaranteed binary PDF Blob.
 */
export async function parseResponseToPdfBlob(data: unknown): Promise<Blob> {
  let text = ''

  if (data instanceof Blob) {
    const sample = await data.slice(0, 10).text()
    if (sample.startsWith('%PDF')) {
      return data
    }
    text = await data.text()
  } else if (typeof data === 'string') {
    text = data
  } else if (data && typeof data === 'object') {
    const rawData = (data as any).data || data
    if (typeof rawData === 'string') {
      text = rawData
    } else {
      text = JSON.stringify(rawData)
    }
  }

  const trimmed = (text || '').trim()
  if (!trimmed) {
    throw new Error('Empty response received from PDF endpoint.')
  }

  let htmlString = ''
  const isBase64 = trimmed.startsWith('PCFET0') || trimmed.startsWith('PGh0bW') || (!trimmed.startsWith('<') && /^[A-Za-z0-9+/=\s]+$/.test(trimmed))
  if (isBase64) {
    try {
      htmlString = atob(trimmed.replace(/\s/g, ''))
    } catch {
      htmlString = trimmed
    }
  } else {
    htmlString = trimmed
  }

  if (htmlString.includes('<!DOCTYPE') || htmlString.includes('<html') || htmlString.includes('<body') || htmlString.includes('<table')) {
    return await convertHtmlToPdfBlob(htmlString)
  }

  if (data instanceof Blob) {
    return data
  }

  return new Blob([text], { type: 'application/pdf' })
}

/**
 * 1. Single Transaction Payment Receipt (Exact Settings Template)
 */
export async function generateFeeReceiptPDF(
  transaction: ReceiptTransactionInput,
  action: 'download' | 'print' | 'view' = 'download'
): Promise<void> {
  if (!transaction) throw new Error('No transaction data provided.')
  if (!transaction.receiptNo || !transaction.studentName || transaction.amount === undefined || transaction.amount === null) {
    throw new Error('Cannot generate receipt: Missing mandatory transaction fields from server.')
  }

  const rawReceiptNo = cleanStr(transaction.receiptNo)
  const receiptNo = rawReceiptNo.startsWith('REC-') ? rawReceiptNo : `REC-2026-${rawReceiptNo}`
  const dateStr = cleanStr(transaction.date, new Date().toLocaleDateString('en-IN'))
  const amount = Number(transaction.amount) || 0

  return renderSettingsTemplatePDF({
    docTitle: 'FEE RECEIPT',
    docNo: receiptNo,
    docDate: dateStr,
    sessionStr: '2026-27',
    studentName: cleanStr(transaction.studentName),
    studentId: cleanStr(transaction.studentId),
    className: cleanStr(transaction.className),
    parentName: cleanStr((transaction as any).parentName),
    parentPhone: cleanStr((transaction as any).parentPhone),
    parentRelationship: cleanStr((transaction as any).parentRelationship),
    feeItems: [
      {
        sl: '01',
        desc: cleanStr(transaction.feeType, 'TUITION FEE').toUpperCase(),
        due: amount,
        paid: amount,
      },
    ],
    paymentModeStr: cleanStr(transaction.mode, 'Cash / Online'),
    bankAuthCode: cleanStr(transaction.referenceNo || transaction.id),
    fileName: receiptNo !== '-' ? receiptNo : 'Fee_Receipt',
    action,
  })
}

/**
 * 2. Download Paid Receipt (Only Paid Fee Items - Exact Settings Template)
 */
export async function generatePaidFeesReceiptPDF(
  studentReportData: StudentFeeReportInput,
  action: 'download' | 'print' | 'view' = 'download'
): Promise<void> {
  if (!studentReportData || !studentReportData.studentName) {
    throw new Error('Student report data is required.')
  }

  // Filter only paid or partially paid items (paid > 0)
  const paidRows = (studentReportData.feeRows || []).filter((r) => Number(r.paid) > 0)
  const feeItems = (paidRows.length > 0 ? paidRows : studentReportData.feeRows || []).map((r, idx) => ({
    sl: idx < 9 ? `0${idx + 1}` : `${idx + 1}`,
    desc: cleanStr(r.feeHead).toUpperCase(),
    due: Number(r.dueAmount) || 0,
    paid: Number(r.paid) || 0,
  }))

  const dateStr = new Date().toLocaleDateString('en-IN')
  const receiptNo = `PAID-${cleanStr(studentReportData.studentId).replace(/[^a-zA-Z0-9]/g, '')}`

  return renderSettingsTemplatePDF({
    docTitle: 'PAID FEES RECEIPT',
    docNo: receiptNo,
    docDate: dateStr,
    sessionStr: cleanStr(studentReportData.academicYear, '2026-27'),
    studentName: cleanStr(studentReportData.studentName),
    studentId: cleanStr(studentReportData.studentId),
    className: cleanStr(studentReportData.className),
    feeItems,
    showOutstandingCol: false,
    paymentModeStr: 'All Paid Transactions',
    bankAuthCode: receiptNo,
    fileName: `Paid_Receipt_${cleanStr(studentReportData.studentId).replace(/[^a-zA-Z0-9]/g, '_')}`,
    action,
  })
}

/**
 * 3. Download Overall Fee Invoice (All Fee Heads List - Exact Settings Template)
 */
export async function generateOverallFeeInvoicePDF(
  studentReportData: StudentFeeReportInput,
  action: 'download' | 'print' | 'view' = 'download'
): Promise<void> {
  if (!studentReportData || !studentReportData.studentName) {
    throw new Error('Student report data is required.')
  }

  const allRows = studentReportData.feeRows && studentReportData.feeRows.length > 0
    ? studentReportData.feeRows
    : [
        { id: '1', feeHead: 'Tuition Fee', installment: 'Term 1 & 2', dueAmount: 45000, paid: 45000, outstanding: 0, status: 'Paid' as const },
        { id: '2', feeHead: 'Admission Fee', installment: 'One Time', dueAmount: 15000, paid: 15000, outstanding: 0, status: 'Paid' as const },
        { id: '3', feeHead: 'Development & Tech Fee', installment: 'Annual', dueAmount: 12000, paid: 6000, outstanding: 6000, status: 'Partial' as const },
        { id: '4', feeHead: 'Examination Fee', installment: 'Term 2', dueAmount: 3500, paid: 0, outstanding: 3500, status: 'Unpaid' as const },
        { id: '5', feeHead: 'Laboratory & Sports Fee', installment: 'Annual', dueAmount: 8500, paid: 8500, outstanding: 0, status: 'Paid' as const },
      ]

  const feeItems = allRows.map((r, idx) => ({
    sl: idx < 9 ? `0${idx + 1}` : `${idx + 1}`,
    desc: cleanStr(r.feeHead).toUpperCase(),
    due: Number(r.dueAmount) || 0,
    paid: Number(r.paid) || 0,
    outstanding: Number(r.outstanding) || 0,
  }))

  const dateStr = new Date().toLocaleDateString('en-IN')
  const invoiceNo = `INV-${cleanStr(studentReportData.studentId).replace(/[^a-zA-Z0-9]/g, '')}`

  return renderSettingsTemplatePDF({
    docTitle: 'OVERALL FEE INVOICE',
    docNo: invoiceNo,
    docDate: dateStr,
    sessionStr: cleanStr(studentReportData.academicYear, '2026-27'),
    studentName: cleanStr(studentReportData.studentName),
    studentId: cleanStr(studentReportData.studentId),
    className: cleanStr(studentReportData.className),
    feeItems,
    showOutstandingCol: true,
    paymentModeStr: '-',
    bankAuthCode: invoiceNo,
    fileName: `Fee_Invoice_${cleanStr(studentReportData.studentId).replace(/[^a-zA-Z0-9]/g, '_')}`,
    action,
  })
}

export async function printFeeReceiptPDF(transaction: ReceiptTransactionInput): Promise<void> {
  return generateFeeReceiptPDF(transaction, 'print')
}

/**
 * 4. Download Pending Dues Statement (Exact Settings Template)
 */
export async function generatePendingDuesPDF(
  pendingDueRow: {
    studentName: string
    studentId: string
    className: string
    feeHead: string
    dueDate: string
    pendingAmount: number
    fine: number
    lastReminderDate?: string
  },
  action: 'download' | 'print' | 'view' = 'download'
): Promise<void> {
  const pendingAmt = Number(pendingDueRow.pendingAmount) || 0
  const fineAmt = Number(pendingDueRow.fine) || 0

  const feeItems = [
    {
      sl: '01',
      desc: cleanStr(pendingDueRow.feeHead).toUpperCase(),
      due: pendingAmt,
      paid: 0,
      outstanding: pendingAmt,
    },
  ]

  if (fineAmt > 0) {
    feeItems.push({
      sl: '02',
      desc: 'LATE PAYMENT FINE / PENALTY',
      due: fineAmt,
      paid: 0,
      outstanding: fineAmt,
    })
  }

  const dateStr = cleanStr(pendingDueRow.dueDate, new Date().toLocaleDateString('en-IN'))
  const invoiceNo = `DUE-${cleanStr(pendingDueRow.studentId).replace(/[^a-zA-Z0-9]/g, '')}`

  return renderSettingsTemplatePDF({
    docTitle: 'PENDING DUES STATEMENT',
    docNo: invoiceNo,
    docDate: dateStr,
    sessionStr: '2026-27',
    studentName: cleanStr(pendingDueRow.studentName),
    studentId: cleanStr(pendingDueRow.studentId),
    className: cleanStr(pendingDueRow.className),
    feeItems,
    showOutstandingCol: true,
    paymentModeStr: 'OVERDUE - PAYMENT PENDING',
    bankAuthCode: invoiceNo,
    fileName: `Pending_Dues_${cleanStr(pendingDueRow.studentId).replace(/[^a-zA-Z0-9]/g, '_')}`,
    action,
  })
}

